import React, { useEffect, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useINGContextData } from "../Context";
import { getCityDepotContract } from "../ingService";
import CustomSelect from "./CustomSelect";
import styles from "../ING.module.css";

const initial = {
  search: "",
  city: [],
  depot: [],
  contract: [],
};

const makeOptions = (data, key) =>
  [...new Set(data.map((x) => x[key]).filter(Boolean))].map((x) => ({
    label: x,
    value: x,
  }));

// The search box takes a comma-separated string for convenience, but the
// backend APIs expect it as an array of exact values, e.g.
// ["MAT561004P0N01275","MAT561005PMP00861"].
const toSearchArray = (value) =>
  value
    ? value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

export default function CustomFilter({ onApply, onReset }) {
  const { setGlobalFilters } = useINGContextData();
  const [filters, setFilters] = useState(initial);
  const [data, setData] = useState([]);
  const [options, setOptions] = useState({ city: [], depot: [], contract: [] });

  // Whenever the search box changes, re-fetch City/Depot/Contract scoped to
  // those vehicle IDs (debounced so it doesn't fire on every keystroke); an
  // empty search falls back to the full, unfiltered location list.
  useEffect(() => {
    const searchValues = toSearchArray(filters.search);
    const body = searchValues.length ? { search: searchValues } : {};

    const timeoutId = setTimeout(() => {
      getCityDepotContract(body)
        .then((res) => setData(res?.data || []))
        .catch(() => setData([]));
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  useEffect(() => {
    setOptions({
      city: makeOptions(data, "city"),
      depot: makeOptions(data, "depot"),
      contract: makeOptions(data, "contract"),
    });
  }, [data]);

  const related = (selected, key) => {
    const values = (selected || []).map((x) => x.value);

    if (!values.length) {
      return {
        city: makeOptions(data, "city"),
        depot: makeOptions(data, "depot"),
        contract: makeOptions(data, "contract"),
      };
    }

    const rows = data.filter((x) => values.includes(x[key]));
    return {
      city: makeOptions(rows, "city"),
      depot: makeOptions(rows, "depot"),
      contract: makeOptions(rows, "contract"),
    };
  };

  const updateFilter = (name, selected) => {
    const selectedItems = selected || [];
    const r = related(selectedItems, name);

    setOptions((prev) => ({
      ...prev,
      city: name === "city" ? prev.city : r.city,
      depot: name === "depot" ? prev.depot : r.depot,
      contract: name === "contract" ? prev.contract : r.contract,
    }));

    setFilters((prev) => ({
      ...prev,
      [name]: selectedItems,
      city: name === "city" ? selectedItems : r.city,
      depot: name === "depot" ? selectedItems : r.depot,
      contract: name === "contract" ? selectedItems : r.contract,
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...filters, search: toSearchArray(filters.search) };
    setGlobalFilters((prev) => ({ ...prev, ...payload }));
    onApply?.(payload);
  };

  const reset = (e) => {
    e.preventDefault();
    const allOptions = {
      city: makeOptions(data, "city"),
      depot: makeOptions(data, "depot"),
      contract: makeOptions(data, "contract"),
    };

    setOptions(allOptions);
    setFilters(initial);
    setGlobalFilters((prev) => ({
      ...prev,
      ...initial,
      search: [],
      engineStatus: "",
      ignitionStatus: "",
    }));
    onReset?.({ ...initial, search: [], engineStatus: "", ignitionStatus: "" });
  };

  return (
    <div className="container-fluid pt-1 cardBox">
      <div className={`card ${styles.filterCard}`}>
        <div className="card-body busflt">
          <form className={styles.filterRow} onSubmit={submit}>
            <div className={styles.filterFieldWide}>
              <label className={styles.filterLabel}>Search</label>
              <div className={styles.searchWrap}>
                <FaSearch className={styles.searchIcon} size={13} />
                <input
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, search: e.target.value }))
                  }
                  className={`form-control ${styles.searchInput}`}
                  placeholder="Search vehicle ID..."
                  autoComplete="off"
                />
              </div>
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>City</label>
              <CustomSelect
                options={options.city}
                value={filters.city}
                placeholder="All"
                onChange={(v) => updateFilter("city", v)}
              />
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Depot</label>
              <CustomSelect
                options={options.depot}
                value={filters.depot}
                placeholder="All"
                onChange={(v) => updateFilter("depot", v)}
              />
            </div>

            <div className={styles.filterField}>
              <label className={styles.filterLabel}>Contract</label>
              <CustomSelect
                options={options.contract}
                value={filters.contract}
                placeholder="All"
                onChange={(v) => updateFilter("contract", v)}
              />
            </div>

            <div className={styles.filterActions}>
              <button className="fleet-btn fleet-btn-sm blue-theme h-50 custom-Btn-animation">
                Apply
              </button>
              <button
                type="button"
                onClick={reset}
                className="fleet-btn fleet-btn-sm gray-theme h-50 custom-Btn-animation"
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
